import { addRole, updateRole } from "api/userManagement";
import {
  clearPermissionSettings,
  setEditRole,
  setPermissionByCategory,
  setRolesDetailsData,
  setRolesPermissionViewOnly,
  setSelectedPermissions,
  toggleRoleModal,
} from "pages/ManageRoles/roleSlice";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ModalComp from "themes/elements/Modal";
import TreeComp from "themes/elements/Tree";
import { retrieveUserData } from "utils/cryptoStorage";
import { isPermit } from "utils/permissionCheck";

import ModalFooter from "../modalFooter";
function Permission({ open, setOpen, isEdit, fetchRoles }) {
  const userToken = retrieveUserData();
  let admin_user_id, admin_user_name;

  if (userToken) {
    ({ admin_user_id, admin_user_name } = userToken);

    useEffect(() => {
      if (!isEdit && !rolePermissionViewOnly) {
        dispatch(setSelectedPermissions([]));
      }
    }, [open, isEdit]);
  }
  const dispatch = useDispatch();
  const {
    permissionSettings,
    rolePermissionViewOnly,
    roleDetails,
    permissionsData,
    selectedRole,
  } = useSelector((state) => state.roles);

  const [saving, setSaving] = useState(false);

  function findPermissionsByIds(permissionNames) {
    const permissionsMap = [];
    permissionsData.forEach((section) => {
      section.data.forEach((module) => {
        module.children.forEach((permission) => {
          if (permissionNames.includes(permission.value)) {
            permissionsMap.push({
              permission_id: permission.id,
              module_id: module.id,
            });
          }
        });
      });
    });
    return permissionsMap;
  }

  // Assuming selectedPermissions holds the permission labels
  const roleManagementWithIds = findPermissionsByIds(
    permissionSettings?.roleManagement?.map((perm) => perm)
  );
  const userManagementWithIds = findPermissionsByIds(
    permissionSettings?.userManagement?.map((perm) => perm)
  );
  const newsManagementWithIds = findPermissionsByIds(
    permissionSettings?.newsManagement?.map((perm) => perm)
  );
  const notificationManagementWithIds = findPermissionsByIds(
    permissionSettings?.notificationManagement?.map((perm) => perm)
  );
  const wardManagementWithIds = findPermissionsByIds(
    permissionSettings?.wardManagement?.map((perm) => perm)
  );
  const incidentManagementWithIds = findPermissionsByIds(
    permissionSettings?.incidentManagement?.map((perm) => perm)
  );
  const counsilorManagementWithIds = findPermissionsByIds(
    permissionSettings?.counsilorManagement?.map((perm) => perm)
  );
  const projectManagementWithIds = findPermissionsByIds(
    permissionSettings?.projectManagement?.map((perm) => perm)
  );
  function handleClose(fromButton = false) {
    //
    // Group permissions based on module_id
    const groupedPermissions = {
      roleManagement: [],
      userManagement: [],
      notificationManagement: [],
      newsManagement: [],
      wardManagement: [],
      incidentManagement: [],
      counsilorManagement: [],
      projectManagement: [],
    };

    selectedRole?.permissions.forEach((permission) => {
      switch (permission?.module_id) {
        case 1:
          groupedPermissions.roleManagement.push(permission.permission_name);
          break;
        case 2:
          groupedPermissions.userManagement.push(permission.permission_name);
          break;
        case 3:
          groupedPermissions.newsManagement.push(permission.permission_name);
          break;
        case 4:
          groupedPermissions.notificationManagement.push(
            permission.permission_name
          );
          break;
        case 5:
          groupedPermissions.wardManagement.push(permission.permission_name);
          break;
        case 6:
          groupedPermissions.incidentManagement.push(
            permission.permission_name
          );
          break;
        case 7:
          groupedPermissions.counsilorManagement.push(
            permission.permission_name
          );
          break;
        case 8:
          groupedPermissions.projectManagement.push(permission.permission_name);
          break;
        default:
          break;
      }
    });

    // Dispatch updates for each category
    dispatch(
      setPermissionByCategory({
        category: "roleManagement",
        permissions: groupedPermissions.roleManagement,
      })
    );
    dispatch(
      setPermissionByCategory({
        category: "userManagement",
        permissions: groupedPermissions.userManagement,
      })
    );
    dispatch(
      setPermissionByCategory({
        category: "notificationManagement",
        permissions: groupedPermissions.notificationManagement,
      })
    );
    dispatch(
      setPermissionByCategory({
        category: "newsManagement",
        permissions: groupedPermissions.newsManagement,
      })
    );

    dispatch(
      setPermissionByCategory({
        category: "wardManagement",
        permissions: groupedPermissions.wardManagement,
      })
    );

    dispatch(
      setPermissionByCategory({
        category: "incidentManagement",
        permissions: groupedPermissions.incidentManagement,
      })
    );
    dispatch(
      setPermissionByCategory({
        category: "counsilorManagement",
        permissions: groupedPermissions.counsilorManagement,
      })
    );
    dispatch(
      setPermissionByCategory({
        category: "projectManagement",
        permissions: groupedPermissions.projectManagement,
      })
    );

    const permissionsForDispatch = selectedRole?.permissions.map(
      (permission) => permission.permission_name
    );
    dispatch(setSelectedPermissions(permissionsForDispatch));
    if (!isEdit) {
      dispatch(clearPermissionSettings());
    }
    setOpen(false);
  }

  const handleSave = async () => {
    const permissionsWithIds = [
      ...roleManagementWithIds,
      ...userManagementWithIds,
      ...newsManagementWithIds,
      ...notificationManagementWithIds,
      ...wardManagementWithIds,
      ...incidentManagementWithIds,
      ...counsilorManagementWithIds,
      ...projectManagementWithIds,
    ];

    if (permissionsWithIds.length === 0) {
      toast.warning("At least one permission must be selected.");
      return; // Exit if no permissions are selected
    }

    setSaving(true); // Set loading state to true

    const addRoleData = {
      role_name: roleDetails.role_name,
      role_description: roleDetails.role_description,
      role_status_flg: roleDetails.role_status_flg,
      permissions: permissionsWithIds,
    };

    const updateRoleData = {
      role_id: roleDetails?.role_id || 1,
      role_name: roleDetails.role_name,
      role_description: roleDetails.role_description,
      role_status_flg: roleDetails.role_status_flg,
      permissions: permissionsWithIds,
    };

    try {
      let response;
      if (isEdit) {
        response = await updateRole(updateRoleData);
        if (response?.message) {
          dispatch(toggleRoleModal(false));
          toast.success(response.message);
          fetchRoles();
        }
      } else {
        response = await addRole(addRoleData);
        if (response?.message) {
          dispatch(toggleRoleModal(false));
          toast.success(response.message);
          fetchRoles();
        }
      }
      setOpen(false); // Close modal on success
    } catch (error) {
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalComp
      title={
        isEdit
          ? "Edit permissions"
          : rolePermissionViewOnly
          ? "View Permission"
          : "Set permissions"
      }
      footer={
        <ModalFooter
          handlePrimaryBtn={() => handleClose(true)}
          hidePrimaryBtn={rolePermissionViewOnly}
          primaryBtnVariant="secondary"
          primaryBtnText={`${rolePermissionViewOnly ? "tghjk" : "Cancel"}`}
          secondaryBtnText={`${
            rolePermissionViewOnly ? "Edit permissions" : "Save"
          }`}
          hideSecondaryBtn={
            rolePermissionViewOnly ? !isPermit(1, "edit") : false
          }
          secondaryBtnLoading={saving}
          handleSecondaryBtn={() => {
            if (rolePermissionViewOnly) {
              dispatch(setRolesDetailsData(selectedRole));
              dispatch(setEditRole(true));
              dispatch(setRolesPermissionViewOnly(false));
            } else {
              handleSave();
            }
          }}
        />
      }
      open={open}
      setOpen={handleClose}
    >
      <div className="flex flex-column gap-4">
        {permissionsData.map((permission) => (
          <TreeComp
            data={permission.data}
            viewOnly={rolePermissionViewOnly}
            category={permission.category}
          />
        ))}
      </div>
    </ModalComp>
  );
}

export default Permission;
